/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useMutation } from 'react-query';
import {
    func, shape, object, string, bool,
} from 'prop-types';
import { Button, CircularProgress } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
// import 'react-day-picker/dist/style.css';

import { withStyles } from '@material-ui/core/styles';

import { ModifyDatesStyle as calendarStyle } from '../../../../style/ModifyDatesStyle';
import fetchDeliveryDatesCalendar from '../../../../../../../apis/mmo-apis/fetchDeliveryDatesCalendar';
import { getAccessTokenSafely } from '../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { mmmmDDYYYY } from '../../../../utils/dateString';
import { getIsPassportUserLoggedIn } from '../../../../../../../state/ducks/Checkout/ducks/Global/Global-Selectors';

const styles = ({ palette, breakpoints }) => calendarStyle(palette, breakpoints);


const ModifyDateV2 = ({
    ROSSOrder,
    classes,
    modifyEligibility,
    originalDate,
    changeTabs,
    modifiedDate,
    setModifiedDate,
    setDeliverySLA,
    ...props
}) => {
    const [availableDeliveryDates, setAvailableDeliveryDates] = useState([]);
    const [fetchDeliveryDatesError, setFetchDeliveryDatesError] = useState('');
    
    const isPassportUser = useSelector(getIsPassportUserLoggedIn);

    const [firstAvailableDate, lastAvailableDate] = [availableDeliveryDates[0]?.date, availableDeliveryDates[availableDeliveryDates.length - 1]?.date];
    const addressDetail = ROSSOrder?.recipientProfiles?.[0]?.addressDetail[0];
    const item = ROSSOrder?.items?.[0];
    const [state, zipCode] = props.isRecipientAddressChanged
        ? [props.modifiedRecipientAddress.state, props.modifiedRecipientAddress.zipCode]
        : [addressDetail.state, addressDetail.zipCode];
    const requestBodyV2 = {
        intOrderNumber: ROSSOrder.intOrderNumber,
        brandCode: item.productBrand,
        productSku: item.productSku,
        locationType: props.locationType,
        state,
        country: 'USA',
        zipCode,
        itemQuantity: item.quantity,
        customerType: isPassportUser ? 'P' : 'G',
        channel: '99998'
    }

    const fetchDeliveryCalendarDatesMutation = useMutation({
        mutationFn: fetchDeliveryDatesCalendar,
        onSuccess: ({ data: { deliveryDates } }) => {
            if (deliveryDates?.length > 0) {
                setAvailableDeliveryDates(deliveryDates);
            } else if (deliveryDates.length === 0) {
                setFetchDeliveryDatesError("We're sorry, there are no available delivery dates for this product.");
            } else {
                setFetchDeliveryDatesError("We've encountered an error. Please try again.");
            }
        },
        onError: () => {
            setFetchDeliveryDatesError("We've encountered an error. Please try again.");
        }
    })
    useEffect(() => {
        const callDeliveryDatesAPI = async () => {
            const jwtToken = await getAccessTokenSafely();
            fetchDeliveryCalendarDatesMutation.mutate({
                requestBody: requestBodyV2,
                jwtToken,
            });
        }
        callDeliveryDatesAPI();
    }, []);

    const goToNextTab = () => {
        if (modifyEligibility?.isCardMsgModifiable === false) {
            props.checkForConfirmationScreen(true);
            setModifiedDate(modifiedDate); // TODO: double check here - may be buggy
        } else {
            setModifiedDate(modifiedDate);
            changeTabs(2);
        }
    };

    const findObjectByDate = (array, targetDate) => {
        const formattedDate = format(new Date(targetDate), 'yyyy-MM-dd');
        return array.find(item => item.date === formattedDate);
    }

    if (fetchDeliveryCalendarDatesMutation.isLoading) {
        return (
            <div className={classes.loadingContainer}>
                <p>Searching for available delivery dates...</p>
                <CircularProgress color="inherit" />
            </div>
        );
    }

    const availableDeliveryDatesObject = {};
    availableDeliveryDates.forEach(({ date }) => availableDeliveryDatesObject[date] = true);
    const generateFooter = () => (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <h5>
                    Current delivery date
                    <p>{mmmmDDYYYY(item.deliveryDate)}</p>
                </h5>
                <h5>
                    New delivery date
                    <p>
                        {modifiedDate && modifiedDate !== item.deliveryDate ? mmmmDDYYYY(modifiedDate) : 'No new delivery date selected'}
                    </p>
                </h5>
            </div>
        )

    return (
        <>
            <Helmet>
                <link href="https://cdn.jsdelivr.net/npm/react-day-picker@8.7.1/dist/style.min.css" rel="stylesheet" />
            </Helmet>
            {
                fetchDeliveryDatesError ? (
                    <div className={classes.deliveryDateBlank}>
                        <p>{fetchDeliveryDatesError}</p>
                    </div>
                ) : (
                    <div className={classes.dayPickerContainer}>
                        <DayPicker
                            mode='single'
                            fromMonth={new Date(firstAvailableDate)}
                            toMonth={new Date(lastAvailableDate)}
                            selected={new Date(modifiedDate)}
                            onSelect={(setDate) => {
                              if (setDate && setDate !== item.deliveryDate) {
                                 setModifiedDate(setDate);
                                const { deliverySLA } = findObjectByDate(availableDeliveryDates, setDate) || {};
                                setDeliverySLA(deliverySLA || '');
                              }
                            }}
                            showOutsideDays
                            disabled={(date) => !availableDeliveryDatesObject[format(date, 'yyyy-MM-dd')]}
                        />
                        {generateFooter()}
                    </div>
                )
            }
            <div className={classes.CTAButtonContainer}>
                <Button
                    className={`${classes.CTAButtons} ${classes.goBackButton}`}
                    variant="outlined"
                    fullWidth
                    disabled={!modifyEligibility?.isRecipientModifiable}
                    onClick={() => changeTabs(0)}
                >Go Back
                </Button>
                <Button className={`${classes.CTAButtons} ${classes.nextButton}`} variant="contained" fullWidth color="primary" onClick={goToNextTab}>Next</Button>
            </div>
        </>
    );
};

ModifyDateV2.propTypes = {
    ROSSOrder: object.isRequired,
    locationType: string.isRequired,
    classes: object.isRequired,
    changeTabs: func.isRequired,
    originalDate: string.isRequired,
    isRecipientAddressChanged: bool.isRequired,
    modifiedRecipientAddress: object.isRequired,
    modifyEligibility: shape({
        isRecipientModifiable: bool.isRequired,
        isDeliveryDateModifiable: bool.isRequired,
        isCardMsgModifiable: bool.isRequired,
    }).isRequired,
    checkForConfirmationScreen: func.isRequired,
    modifiedDate: object.isRequired,
    setModifiedDate: func.isRequired,
    setDeliverySLA: func.isRequired,
};

const StyledModifyDateV2 = withStyles(styles)(ModifyDateV2);
export default StyledModifyDateV2;
